import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LeaveTypeMaster,
  NamedLeavePolicy,
  LeavePolicyAllocation,
  AccrualFrequency,
} from '../../../types/leavePolicy';
import { LeavePolicyService } from '../../../services/leavePolicyService';
import { LeaveBalanceService } from '../../../services/leaveBalanceService';
import { LeaveBalanceSnapshot } from '../../../types/leavePolicy';
import { compareByEmployeeId, loadAllUsers, SimpleUser } from '../../../services/userLoader';
import { OrgConfigService } from '../../../services/orgConfigService';
import { LeaveService } from '../../../services/leave';
// The shared staff/administrator rule, kept with the roster service so Leave and Shift Roster
// cannot disagree about who belongs in a roster or an entitlement sheet.
import { isAdministrativeRole } from '../../../services/shiftRoster';
import { LeaveRequest } from '../../../types/leave';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveActorId } from '../../../services/identity';
import EmployeeLeaveProfileDrawer from '../EmployeeLeaveProfileDrawer';
import LeaveBalanceAdjustModal from '../LeaveBalanceAdjustModal';
import { YearSelect, formatLeaveDate, leaveTypeName, balanceLevel } from './leaveShared';
import { expirySummary, expiryTooltip } from '../leaveExpiry';
import { usedTooltip } from '../leaveUsed';
import { LeaveAccrualRates } from '../../../services/leaveAccrualEngine';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// balanceLevel now lives in leaveShared, so the Leave Dashboard's usage meter colours by the
// same thresholds this sheet does. Its reasoning moved with it.

/**
 * What the employee will have accrued by the end of each calendar month (12 values).
 *
 * EL/CL/SL are prorated against the caps the employee’s policy sets — the same caps the
 * balance maths uses, so this row and the Entitlement beside it cannot disagree. ML/CO and
 * custom types keep the legacy monthly proration from grantedPerYear.
 *
 * ── Projected to December, not frozen at today ───────────────────────────────────────
 *
 * This used to stop accruing at the current month and then REPEAT that figure across the
 * remaining ones, so an intern who joined in September read "1 1 1 1" for Sep–Dec beside an
 * Entitlement of 2: the months showed what had accrued so far while the column beside them
 * showed the whole year, and the row looked like it did not add up. December now equals the
 * entitlement, which is the question this table is actually asked.
 *
 * Uses computeEarnedMonths’ rules from leaveAccrualEngine — the single source of truth.
 */
const monthlyCumulativeNew = (
  dateOfJoining: string,
  year: number,
  leaveTypeCode: string,
  /* ML, CO and custom types: their own days-per-month, resolved by the caller. */
  otherMonthlyRate: number,
  rates: LeaveAccrualRates
): number[] => {
  const result = new Array(12).fill(0);
  const d = new Date(dateOfJoining);
  if (isNaN(d.getTime())) return result;
  const dojYear = d.getFullYear();
  const dojMonth = d.getMonth();
  const dojDay = d.getDate();
  const now = new Date();
  const currentYear = now.getFullYear();
  if (year > currentYear || year < dojYear) return result;
  /* December, always — see the projection note above. */
  const lastMonth = 11;
  const code = leaveTypeCode.toUpperCase();

  if (code === 'EL' || code === 'CL' || code === 'SL') {
    // Formula-driven: compute earnedMonths at each cumulative month endpoint.
    let credits = 0;
    for (let m = 0; m < 12; m++) {
      if (m <= lastMonth) {
        if (year === dojYear) {
          if (m === dojMonth) credits += dojDay <= 15 ? 1 : 0.5;
          else if (m > dojMonth) credits += 1;
        } else {
          credits += 1;
        }
        credits = Math.min(12, credits);
      }
      /* grantsFromRates, month by month — EL unrounded, CL and SL to whole days. */
      if (code === 'EL') {
        result[m] = credits * rates.el;
      } else if (code === 'CL') {
        result[m] = Math.round(credits * rates.cl);
      } else {
        result[m] = Math.round(credits * rates.sl);
      }
    }
  } else {
    // ML, CO and custom types, at the rate the caller resolved.
    const monthlyRate = otherMonthlyRate;
    let credits = 0;
    for (let m = 0; m < 12; m++) {
      if (m <= lastMonth) {
        if (year === dojYear) {
          if (m === dojMonth) credits += dojDay <= 15 ? 1 : 0.5;
          else if (m > dojMonth) credits += 1;
        } else {
          credits += 1;
        }
        credits = Math.min(12, credits);
      }
      result[m] = Math.round(monthlyRate * credits * 100) / 100;
    }
  }
  return result;
};

type PolicySection = 'TYPES' | 'POLICIES' | 'ASSIGNMENT';

const emptyLeaveType: Omit<LeaveTypeMaster, 'id'> = {
  code: '', name: '', description: '', color: '#0d9488', isPaid: true, isActive: true, grantedPerYear: 0,
};

/**
 * HR's configured list unioned with the values actually present on employee records.
 *
 * The configured list is the authority on what exists, so a team or employment type with no
 * members yet still has to be offerable — that absence is what made the filters look half
 * populated. The in-use values are unioned in so a legacy value sitting on a record but missing
 * from the config does not become unfilterable. Case-insensitive dedupe, keeping the configured
 * spelling, so "Platform" and "platform" are one entry rather than two.
 */
const mergeOrgList = (configured: string[], inUse: (string | undefined)[]): string[] => {
  const seen = new Map<string, string>();
  [...configured, ...inUse.filter(Boolean) as string[]].forEach((value) => {
    const key = value.trim().toLowerCase();
    if (key && !seen.has(key)) seen.set(key, value.trim());
  });
  return Array.from(seen.values()).sort();
};

const FORMULA_DRIVEN_CODES = new Set(['EL', 'CL', 'SL']);
const isFormulaDrivenType = (code: string) => FORMULA_DRIVEN_CODES.has(code.toUpperCase());

const LeavePoliciesPage: React.FC = () => {
  const [section, setSection] = useState<PolicySection>('TYPES');

  return (
    <section className="lp-team-page">
      {/* ── Page Header ── */}
      <div className="lp-team-header">
        <div>
          <h2 className="lp-team-title">Leave Policies</h2>
          <p className="lp-team-subtitle">
            Configure leave types, multi-allocation policies, and assign policies to employees or departments.
          </p>
        </div>
        <button
          type="button"
          className="lp-refresh-btn"
          onClick={() => window.location.reload()}
        >
          🔄 Refresh
        </button>
      </div>

      {/* ── Horizontal Underline Tabs (EMS Dashboard Style) ── */}
      <div className="lp-tabs">
        <button
          type="button"
          className={`lp-tab ${section === 'TYPES' ? 'active' : ''}`}
          onClick={() => setSection('TYPES')}
        >
          Leave Types
        </button>
        <button
          type="button"
          className={`lp-tab ${section === 'POLICIES' ? 'active' : ''}`}
          onClick={() => setSection('POLICIES')}
        >
          Leave Policies
        </button>
        <button
          type="button"
          className={`lp-tab ${section === 'ASSIGNMENT' ? 'active' : ''}`}
          onClick={() => setSection('ASSIGNMENT')}
        >
          Leave Assignment
        </button>
      </div>

      {section === 'TYPES' && <LeaveTypesSection />}
      {section === 'POLICIES' && <LeavePoliciesSection />}
      {/* The assignment sheet needs to send HR to the sections that actually own the granted
          allocation, rather than offering to edit it in place. */}
      {section === 'ASSIGNMENT' && <LeaveAssignmentSection onNavigateSection={setSection} />}
    </section>
  );
};

// ---------------- A. Leave Types ----------------
const LeaveTypesSection: React.FC = () => {
  const [types, setTypes] = useState<LeaveTypeMaster[]>(() => LeavePolicyService.getLeaveTypes());
  const [editing, setEditing] = useState<LeaveTypeMaster | Omit<LeaveTypeMaster, 'id'> | null>(null);
  const [isNew, setIsNew] = useState(false);

  const refresh = () => setTypes(LeavePolicyService.getLeaveTypes());

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (isNew) {
      LeavePolicyService.createLeaveType(editing as Omit<LeaveTypeMaster, 'id'>);
    } else {
      LeavePolicyService.updateLeaveType(editing as LeaveTypeMaster);
    }
    refresh();
    setEditing(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this leave type? This cannot be undone.')) {
      LeavePolicyService.deleteLeaveType(id);
      refresh();
    }
  };

  if (editing) {
    return (
      <form onSubmit={handleSave} className="lb-policy-form">
        <h3>{isNew ? 'Create Leave Type' : `Edit Leave Type: ${(editing as LeaveTypeMaster).code}`}</h3>
        <div className="lb-form-row">
          <div className="lb-form-group">
            <label>Code</label>
            <input type="text" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} required maxLength={5} />
          </div>
          <div className="lb-form-group">
            <label>Name</label>
            <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
          </div>
        </div>
        <div className="lb-form-group">
          <label>Description</label>
          <input type="text" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
        </div>
        {isFormulaDrivenType(editing.code) && (
          <div className="lb-form-note" role="note">
            {editing.code.toUpperCase()} entitlement is auto-calculated from each employee's date of
            joining (earned-months formula) — "Days Per Year" isn't used for this type.
          </div>
        )}
        <div className="lb-form-row">
          <div className="lb-form-group">
            <label>Color</label>
            <input type="color" value={editing.color || '#0d9488'} onChange={(e) => setEditing({ ...editing, color: e.target.value })} />
          </div>
          <div className="lb-form-group">
            <label>Days Per Year</label>
            <input
              type="number"
              min={0}
              value={editing.grantedPerYear}
              onChange={(e) => setEditing({ ...editing, grantedPerYear: Number(e.target.value) })}
              disabled={isFormulaDrivenType(editing.code)}
              required
            />
          </div>
        </div>
        <div className="lb-form-checkbox-row">
          <label>
            <input type="checkbox" checked={editing.isPaid} onChange={(e) => setEditing({ ...editing, isPaid: e.target.checked })} />
            Paid Leave
          </label>
          <label>
            <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
            Active
          </label>
        </div>
        <div className="lb-form-actions">
          <button type="button" className="lb-btn-cancel" onClick={() => setEditing(null)}>Cancel</button>
          <button type="submit" className="lb-btn-submit">Save Leave Type</button>
        </div>
      </form>
    );
  }

  return (
    <div className="lp-section">
      <div className="lp-action-bar" style={{ marginBottom: 18 }}>
        <button
          type="button"
          className="lp-btn-primary"
          onClick={() => { setEditing({ ...emptyLeaveType }); setIsNew(true); }}
        >
          + New Leave Type
        </button>
      </div>

      <div className="lp-table-card">
        <div className="lp-table-wrapper">
          <table className="lp-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Days/Year</th>
                <th>Paid?</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id}>
                  <td><strong style={{ color: t.color }}>{t.code}</strong></td>
                  <td>{t.name}</td>
                  <td>{t.description || '—'}</td>
                  <td>{isFormulaDrivenType(t.code) ? 'Auto-calculated' : t.grantedPerYear}</td>
                  <td>{t.isPaid ? 'Paid' : 'Unpaid'}</td>
                  <td>
                    <span className={t.isActive ? 'lp-badge-active' : 'lp-badge-inactive'}>
                      {t.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td>
                    <div className="lp-actions">
                      <button
                        type="button"
                        className="lp-btn-action-edit"
                        onClick={() => { setEditing(t); setIsNew(false); }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="lp-btn-action-danger"
                        onClick={() => handleDelete(t.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------- B. Leave Policies (named, multi-allocation) ----------------
const emptyNamedPolicy = (types: LeaveTypeMaster[]): Omit<NamedLeavePolicy, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: '',
  description: '',
  allocations: types.map((t) => ({ leaveTypeCode: t.code, grantedPerYear: 0 })),
  accrualFrequency: 'YEARLY',
  carryForwardAllowed: false,
  maxCarryForward: 0,
  expiryMonths: 0,
  allowNegativeBalance: false,
  probationRestricted: false,
  isActive: true,
});

const LeavePoliciesSection: React.FC = () => {
  const [leaveTypes] = useState<LeaveTypeMaster[]>(() => LeavePolicyService.getLeaveTypes());
  /* The same list User Management offers, so the two screens cannot drift apart. */
  const [employmentTypes] = useState<string[]>(() => OrgConfigService.getEmploymentTypes());
  const [policies, setPolicies] = useState<NamedLeavePolicy[]>(() => LeavePolicyService.getNamedPolicies());
  const [editing, setEditing] = useState<NamedLeavePolicy | Omit<NamedLeavePolicy, 'id' | 'createdAt' | 'updatedAt'> | null>(null);
  const [isNew, setIsNew] = useState(false);

  const refresh = () => setPolicies(LeavePolicyService.getNamedPolicies());

  const setAllocation = (code: string, days: number) => {
    if (!editing) return;
    const allocations: LeavePolicyAllocation[] = editing.allocations.map((a) => (a.leaveTypeCode === code ? { ...a, grantedPerYear: days } : a));
    setEditing({ ...editing, allocations });
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (isNew) {
      LeavePolicyService.createNamedPolicy(editing as Omit<NamedLeavePolicy, 'id' | 'createdAt' | 'updatedAt'>);
    } else {
      LeavePolicyService.updateNamedPolicy(editing as NamedLeavePolicy);
    }
    refresh();
    setEditing(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this policy? Existing assignments referencing it will remain but be orphaned.')) {
      LeavePolicyService.deleteNamedPolicy(id);
      refresh();
    }
  };

  if (editing) {
    return (
      <form onSubmit={handleSave} className="lb-policy-form">
        <h3>{isNew ? 'Create Leave Policy' : `Edit Policy: ${(editing as NamedLeavePolicy).name}`}</h3>
        <div className="lb-form-group">
          <label>Policy Name</label>
          <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
        </div>
        <div className="lb-form-group">
          <label>Description</label>
          <input type="text" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
        </div>

        {/*
          * Which employees this policy governs.
          *
          * Without it a policy reaches nobody: the other four assignment scopes (employee,
          * designation, department, location) are set by a screen that was removed, so an
          * "Intern Policy" could sit here marked ACTIVE while every intern was still on the
          * built-in schedule. Left unset, the name is read instead — see policyEmploymentType.
          */}
        <div className="lb-form-row">
          <div className="lb-form-group">
            <label>Applies to Employment Type</label>
            <select
              value={editing.employmentType ?? ''}
              onChange={(e) =>
                setEditing({ ...editing, employmentType: e.target.value || undefined })
              }
            >
              <option value="">— infer from the policy name —</option>
              {employmentTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <small className="lb-form-hint">
              Employees whose User Management record carries this Employment Type get these
              allocations. Earned, Casual and Sick Leave are prorated from each
              employee's Date of Joining.
            </small>
          </div>
        </div>

        <h4>Per-Leave-Type Allocations (days/year)</h4>
        <div className="lb-form-row" style={{ flexWrap: 'wrap' }}>
          {leaveTypes.map((t) => {
            const alloc = editing.allocations.find((a) => a.leaveTypeCode === t.code);
            return (
              <div className="lb-form-group" key={t.code}>
                <label>{t.name} ({t.code})</label>
                <input
                  type="number"
                  min={0}
                  value={alloc?.grantedPerYear ?? 0}
                  onChange={(e) => setAllocation(t.code, Number(e.target.value))}
                />
              </div>
            );
          })}
        </div>

        <div className="lb-form-row">
          <div className="lb-form-group">
            <label>Accrual Frequency</label>
            <select value={editing.accrualFrequency} onChange={(e) => setEditing({ ...editing, accrualFrequency: e.target.value as AccrualFrequency })}>
              <option value="YEARLY">Yearly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </div>
          <div className="lb-form-group">
            <label>Expiry (months after year end, 0 = never)</label>
            <input type="number" min={0} value={editing.expiryMonths} onChange={(e) => setEditing({ ...editing, expiryMonths: Number(e.target.value) })} />
          </div>
        </div>

        <div className="lb-form-checkbox-row">
          <label>
            <input type="checkbox" checked={editing.allowNegativeBalance} onChange={(e) => setEditing({ ...editing, allowNegativeBalance: e.target.checked })} />
            Allow Negative Balance
          </label>
          <label>
            <input type="checkbox" checked={editing.probationRestricted} onChange={(e) => setEditing({ ...editing, probationRestricted: e.target.checked })} />
            Restricted During Probation
          </label>
          <label>
            <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
            Active
          </label>
        </div>

        <div className="lb-form-actions">
          <button type="button" className="lb-btn-cancel" onClick={() => setEditing(null)}>Cancel</button>
          <button type="submit" className="lb-btn-submit">Save Policy</button>
        </div>
      </form>
    );
  }

  return (
    <div className="lp-section">
      <div className="lp-action-bar" style={{ marginBottom: 18 }}>
        <button
          type="button"
          className="lp-btn-primary"
          onClick={() => { setEditing(emptyNamedPolicy(leaveTypes)); setIsNew(true); }}
        >
          + New Policy
        </button>
      </div>

      <div className="lp-table-card">
        <div className="lp-table-wrapper">
          <table className="lp-table">
            <thead>
              <tr>
                <th>Policy Name</th>
                <th>Applies To</th>
                <th>Allocations</th>
                <th>Accrual</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong></td>
                  {/* Resolved, not just the stored field: a policy with no Employment Type set
                      still governs whoever its NAME matches, and showing the stored blank
                      would hide that it is doing so. "Nobody" is the honest answer when it
                      matches no type — that policy is configuration that does nothing. */}
                  <td>{LeavePolicyService.policyEmploymentType(p) || <em>Nobody</em>}</td>
                  <td>{p.allocations.filter((a) => a.grantedPerYear > 0).map((a) => `${leaveTypeName(a.leaveTypeCode)}: ${a.grantedPerYear}`).join(', ') || '—'}</td>
                  <td>{p.accrualFrequency}</td>
                  <td>
                    <span className={p.isActive ? 'lp-badge-active' : 'lp-badge-inactive'}>
                      {p.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td>
                    <div className="lp-actions">
                      <button
                        type="button"
                        className="lp-btn-action-edit"
                        onClick={() => { setEditing(p); setIsNew(false); }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="lp-btn-action-danger"
                        onClick={() => handleDelete(p.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------- C. Leave Assignment ----------------
/**
 * Leave Assignment.
 *
 * The Assign Policy form and the Current Assignments table were removed: every
 * employee already resolves an effective policy through
 * getEffectivePolicyForEmployee, so manual assignment was a second, divergent
 * way of stating the same thing. What remains is the read-only DOJ calculation.
 */
const LeaveAssignmentSection: React.FC<{ onNavigateSection: (section: PolicySection) => void }> = ({
  onNavigateSection,
}) => {
  const [employees, setEmployees] = useState<SimpleUser[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  const loadEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    /*
     * Administrative logins are left out, exactly as they are on the Shift Roster.
     *
     * Super_Admin, HR_Admin and Finance_Admin are operator accounts, not people who accrue leave —
     * they have no joining date, so every one of them sat in this sheet as a row of dashes and a
     * "DOJ not set" warning, which is noise rather than a data gap worth chasing. Same shared rule
     * as the roster, so the two modules cannot disagree about who counts as staff.
     */
    const directory = await loadAllUsers();
    setEmployees(directory.filter(e => !isAdministrativeRole(e.role)));
    setLoadingEmployees(false);
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  return (
    <div className="lp-section">
      <DojLeaveCalculation
        employees={employees}
        loadingEmployees={loadingEmployees}
        onNavigateSection={onNavigateSection}
      />
    </div>
  );
};

// ---- DOJ-based leave calculation view (Leave Assignment) ----
const DojLeaveCalculation: React.FC<{
  employees: SimpleUser[];
  loadingEmployees: boolean;
  onNavigateSection: (section: PolicySection) => void;
}> = ({ employees, loadingEmployees, onNavigateSection }) => {
  const { user } = useAuth();
  const flatPolicies = useMemo(() => LeavePolicyService.getPolicies(), []);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [search, setSearch] = useState('');
  // Team and employment type come from User Management, and both change what an employee is
  // entitled to — employment type drives the Intern leave schedule outright — so being able
  // to slice the assignment sheet by them is the point of recording them.
  const [teamFilter, setTeamFilter] = useState<string>('ALL');
  const [employmentFilter, setEmploymentFilter] = useState<string>('ALL');
  const [balancesByEmail, setBalancesByEmail] = useState<Record<string, LeaveBalanceSnapshot[]>>({});
  const [loadingBalances, setLoadingBalances] = useState(false);

  /*
   * Bumped to force a balance reload after an adjustment is saved, and by the
   * 'leave-balance-updated' event so a grant made anywhere else — the employee profile drawer,
   * another tab — lands here too. A counter rather than calling the loader directly: the loader
   * lives in an effect keyed on the filters, and re-entering it through its own dependency keeps
   * one code path for "fetch the balances" instead of two that can diverge.
   */
  const [balanceEpoch, setBalanceEpoch] = useState(0);

  /** The employee whose full leave profile is open, if any. */
  const [profileEmployee, setProfileEmployee] = useState<SimpleUser | null>(null);
  /**
   * Leave requests, for the profile drawer's history.
   *
   * Fetched on first open rather than with the sheet: the drawer is the only thing that needs
   * them, and the assignment sheet is often used without opening one.
   */
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [leavesLoaded, setLeavesLoaded] = useState(false);

  /** The employee being adjusted, and the leave type the action was taken from. */
  const [adjustEmployee, setAdjustEmployee] = useState<SimpleUser | null>(null);
  const [adjustTypeCode, setAdjustTypeCode] = useState<string>('');

  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);

  /*
   * HR's Organisation Setup, held in state rather than read inline.
   *
   * OrgConfigService's getters are synchronous cache reads, so reading them inside a useMemo
   * would capture whatever was cached at first render and never update — refresh() lands after
   * mount, and its 'org-config-updated' event would have nothing listening. Same pattern as
   * UserManagement, for the same reason.
   */
  const [configuredTeams, setConfiguredTeams] = useState<string[]>(() => OrgConfigService.getTeams());
  const [configuredEmploymentTypes, setConfiguredEmploymentTypes] = useState<string[]>(
    () => OrgConfigService.getEmploymentTypes()
  );

  useEffect(() => {
    const readLists = () => {
      setConfiguredTeams(OrgConfigService.getTeams());
      setConfiguredEmploymentTypes(OrgConfigService.getEmploymentTypes());
    };
    OrgConfigService.refresh().finally(readLists);
    window.addEventListener('org-config-updated', readLists);
    return () => window.removeEventListener('org-config-updated', readLists);
  }, []);

  /*
   * Every team HR has configured, plus any value actually in use.
   *
   * This was derived from the directory alone, on the reasoning that a dropdown should never
   * offer a value matching nobody. The effect was the opposite of helpful: the list HR maintains
   * in Settings → Organisation Setup is the authority on what teams exist, so a team with no
   * members yet — a new one, or one whose people have not been assigned — was simply missing
   * from the filter, and there was nothing on screen to say the list was partial.
   *
   * The union keeps both properties: the configured list is complete, and a legacy value sitting
   * on an employee record but absent from the config still appears rather than becoming
   * unfilterable. Selecting a team with no members shows an empty table, which is a truthful
   * answer and a visible one.
   */
  const teams = useMemo(
    // Deduplicated case-insensitively, keeping the configured spelling where the two differ —
    // otherwise "Platform" on a record and "platform" in the config render as two teams.
    () => mergeOrgList(configuredTeams, employees.map((e) => e.team)),
    [employees, configuredTeams]
  );
  /** Same reasoning as teams above — HR's list, not just the values already assigned. */
  const employmentTypes = useMemo(
    () => mergeOrgList(configuredEmploymentTypes, employees.map((e) => e.employmentType)),
    [employees, configuredEmploymentTypes]
  );

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      /*
       * Searchable on the things somebody can actually type: name, employee number, email.
       *
       * The Employee dropdown beside it lists every person in one flat select, which stops being
       * usable at fifty names — you cannot type into it to narrow it down. This is how you find one
       * person; the dropdown stays for pinning the sheet to them afterwards.
       */
      if (q) {
        const haystack = [e.fullName, e.name, e.employeeId, e.email]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (teamFilter !== 'ALL' && (e.team || '') !== teamFilter) return false;
      if (employmentFilter !== 'ALL' && (e.employmentType || '') !== employmentFilter) return false;
      return true;
    })
      // Ordered by employee number through the shared comparator, so this sheet, User Management
      // and the roster all list people the same way.
      .slice()
      .sort(compareByEmployeeId);
  }, [employees, search, teamFilter, employmentFilter]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingBalances(true);
      const map: Record<string, LeaveBalanceSnapshot[]> = {};
      await Promise.all(filteredEmployees.map(async (e) => {
        try { map[e.email] = await LeaveBalanceService.getBalances(e.email, year); }
        catch { map[e.email] = []; }
      }));
      if (!cancelled) { setBalancesByEmail(map); setLoadingBalances(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [filteredEmployees, year, balanceEpoch]);

  /*
   * An adjustment saved anywhere in the app moves these numbers, so the sheet listens rather than
   * relying on its own save handler alone — LeaveBalanceService.addAdjustment already dispatches
   * this, and the employee profile drawer listens to the same event for the same reason.
   */
  useEffect(() => {
    const bump = () => setBalanceEpoch((n) => n + 1);
    window.addEventListener('leave-balance-updated', bump);
    return () => window.removeEventListener('leave-balance-updated', bump);
  }, []);

  const openProfile = useCallback(async (employee: SimpleUser) => {
    setProfileEmployee(employee);
    if (leavesLoaded) return;
    try {
      const all = await LeaveService.listLeaves();
      setLeaves(all || []);
    } catch {
      // The drawer degrades to balances-only rather than failing to open: the entitlement half
      // is still useful, and an empty history is visibly different from a broken panel.
      setLeaves([]);
    } finally {
      setLeavesLoaded(true);
    }
  }, [leavesLoaded]);

  const handleAdjustSaved = useCallback(
    ({ message, reachedServer }: { message: string; reachedServer: boolean }) => {
      // addAdjustment reports whether the write actually reached AppSync. A local-only grant is
      // invisible to the employee it was made for, so the two outcomes must not read the same.
      setNotice(
        reachedServer
          ? { text: `Adjustment saved — ${message}.`, tone: 'ok' }
          : {
              text:
                `Adjustment recorded on this device only — ${message}. It has not reached the ` +
                'server, so the employee cannot see it yet. Check your connection and re-apply.',
              tone: 'warn',
            }
      );
      setBalanceEpoch((n) => n + 1);
    },
    []
  );

  const adjustLeaveTypes = useMemo(
    () => flatPolicies.map((p) => ({ code: p.leaveTypeCode, name: leaveTypeName(p.leaveTypeCode) })),
    [flatPolicies]
  );

  /*
   * The month-wise accrual breakdown appears when the filters land on exactly one person.
   *
   * Previously it needed the Employee dropdown to be set. That dropdown is gone — the search box
   * replaced it — so the trigger is now simply "the sheet is showing one employee", which is what
   * searching for someone by name or number produces anyway.
   */
  const singleEmployee = filteredEmployees.length === 1 ? filteredEmployees[0] : null;

  /*
   * The caps behind the month-wise table, resolved the same way the balance maths resolves
   * them: the policy that claims this employee, or the defaults for their employment type.
   * Read here rather than inside the row loop so every row of one employee agrees.
   */
  /* The policy governing this employee, for the ML/CO rate below. */
  const singleEmployeePolicy = useMemo(
    () =>
      singleEmployee
        ? LeavePolicyService.getEffectivePolicyForEmployee({
            email: singleEmployee.email,
            department: (singleEmployee as any).department,
            designation: (singleEmployee as any).designation,
            location: (singleEmployee as any).location,
            employmentType: singleEmployee.employmentType,
          })
        : undefined,
    [singleEmployee]
  );

  const singleEmployeeRates: LeaveAccrualRates = useMemo(() => {
    if (!singleEmployee) return LeavePolicyService.ratesFromLeaveTypes();
    return LeavePolicyService.ratesForEmployee({
      email: singleEmployee.email,
      department: (singleEmployee as any).department,
      designation: (singleEmployee as any).designation,
      location: (singleEmployee as any).location,
      employmentType: singleEmployee.employmentType,
    });
  }, [singleEmployee]);

  return (
    <div className="lb-doj-calc">
      <h3 style={{ marginTop: 28 }}>DOJ-Based Leave Calculation</h3>


      {/*
        * Four controls, down from seven.
        *
        * The Employee dropdown is gone: it listed every person in one flat select, which cannot be
        * typed into and stops being usable somewhere around fifty names — the search box does that
        * job properly and does it on employee number and email too. The month-wise breakdown it
        * used to trigger now appears whenever the filters narrow to a single person, so nothing was
        * lost with it.
        *
        * The free-text Department select is gone too: it filtered on the Job Title attribute while
        * the one beside it filtered on the Team list HR actually maintains. Two controls both
        * labelled around "department", filtering different fields, is a trap rather than a feature —
        * the maintained list is the one that survives, labelled Department.
        */}
      <div className="lb-matrix-filters">
        <input
          type="search"
          className="lb-doj-search"
          placeholder="Search name, ID or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search employees"
        />
        <YearSelect value={year} onChange={setYear} />
        {teams.length > 0 && (
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} aria-label="Department">
            <option value="ALL">All Departments</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {employmentTypes.length > 0 && (
          <select value={employmentFilter} onChange={(e) => setEmploymentFilter(e.target.value)} aria-label="Employment type">
            <option value="ALL">All Employment Types</option>
            {employmentTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      {notice && (
        <div className={`lb-doj-notice lb-doj-notice--${notice.tone}`} role="status" aria-live="polite">
          <span>{notice.text}</span>
          <button type="button" className="lb-doj-notice-x" onClick={() => setNotice(null)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/*
        * Where each kind of change belongs.
        *
        * This sheet can adjust a balance, and nothing else — the granted allocation is the policy's
        * and the identity fields are User Management's. Stating that here, with the links, is what
        * stops the absence of an in-place edit reading as a missing feature.
        */}
      {/* One line, not a boxed panel. The point still needs saying — otherwise the absence of an
          in-place edit reads as a missing feature — but it was a third stacked block above a table
          that already carries a legend and a status line. */}
      <p className="lb-doj-scope">
        <strong>Adjust</strong> changes a balance. To change what a leave type grants, edit{' '}
        <button type="button" className="lb-doj-link" onClick={() => onNavigateSection('TYPES')}>
          Leave Types
        </button>{' '}or{' '}
        <button type="button" className="lb-doj-link" onClick={() => onNavigateSection('POLICIES')}>
          Leave Policies
        </button>; for names and departments, <a className="lb-doj-link" href="/super-admin">User Management</a>.
      </p>

      {loadingEmployees && filteredEmployees.length === 0 ? (
        <p className="lb-ineligible-desc">Loading employees...</p>
      ) : filteredEmployees.length === 0 ? (
        <p className="lb-ineligible-desc">No employees match the current filters.</p>
      ) : (
        <>
          {loadingBalances && (
            <p className="lb-doj-legend lb-doj-legend--loading" aria-live="polite">
              Employees are listed below; leave balances are still being calculated.
            </p>
          )}

          {/* Legend, because the cell packs three numbers into a small space. */}
          <p className="lb-doj-legend">
            Each cell shows the <strong>balance</strong> in bold, with{' '}
            <span className="lb-doj-legend-sub">granted · used</span> beneath it.
          </p>

          {/* Summary table: one row per employee */}
          <div className="lb-table-scroll">
            <table className="lb-history-table lb-doj-table">
              <thead>
                <tr>
                  {/* Employee Number leads, then name — the number is the stable
                      identifier people scan for and reconcile against payroll. */}
                  <th className="lb-doj-sticky">Emp No.</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Date of Joining</th>
                  {flatPolicies.map((p) => (
                    <th key={p.leaveTypeCode} className="lb-doj-th-type">
                      <span className="lb-doj-badge">{leaveTypeName(p.leaveTypeCode)}</span>
                    </th>
                  ))}
                  <th className="lb-doj-th-em">Balance</th>
                  <th className="lb-doj-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((e) => {
                  const snaps = balancesByEmail[e.email] || [];
                  const dojMissing = !e.dateOfJoining;
                  const totalBalance = snaps.reduce((sum, s) => sum + s.balance, 0);
                  return (
                    <tr key={e.email} className={dojMissing ? 'lb-doj-row-muted' : undefined}>
                      <td className="lb-doj-sticky lb-doj-empno">{e.employeeId || '—'}</td>
                      <td className="lb-doj-name">
                        {/* A button, not a click handler on the cell: this opens the employee's
                            full leave profile, and an affordance that only reveals itself on
                            hover is one nobody finds. Keyboard-reachable for the same reason. */}
                        <button
                          type="button"
                          className="lb-doj-name-btn"
                          onClick={() => openProfile(e)}
                          title={`Open the full leave profile for ${e.fullName || e.name}`}
                        >
                          {e.fullName || e.name}
                        </button>
                        {e.dateOfExit && (
                          <span className="lb-doj-chip-exit" title="Accrual stops from this month">
                            Exit {formatLeaveDate(e.dateOfExit)}
                          </span>
                        )}
                      </td>
                      <td className="lb-doj-dept">{e.department || '—'}</td>
                      <td className="lb-doj-num">
                        {e.dateOfJoining
                          ? formatLeaveDate(e.dateOfJoining)
                          : <span className="lb-doj-chip-warn">DOJ not set</span>}
                      </td>
                      {flatPolicies.map((p) => {
                        const s = snaps.find((x) => x.leaveTypeCode.toUpperCase() === p.leaveTypeCode.toUpperCase());
                        /*
                         * Three states, not two.
                         *
                         * The table now renders as soon as the directory loads, with balances
                         * arriving afterwards, so "no snapshot yet" has to read differently from
                         * "no entitlement". Collapsing both to an em dash would show a full sheet
                         * of dashes on open and look like every employee had nothing.
                         */
                        if (!s && loadingBalances && !dojMissing) {
                          return (
                            <td key={p.leaveTypeCode} className="lb-doj-cell lb-doj-cell-loading" aria-busy="true">
                              ·
                            </td>
                          );
                        }
                        if (dojMissing || !s) {
                          return <td key={p.leaveTypeCode} className="lb-doj-cell lb-doj-cell-empty">—</td>;
                        }
                        return (
                          <td
                            key={p.leaveTypeCode}
                            className="lb-doj-cell"
                            title={
                              /*
                               * Entitlement and the real used figure, not `granted` and the
                               * bare `consumed` field.
                               *
                               * The system went live mid-year, so leave taken before then is a
                               * negative HR adjustment rather than a leave request. `consumed`
                               * cannot see it and `granted` is reduced by it, so this cell read
                               * "granted 10, used 0, balance 7.5" — three numbers that cannot
                               * all be true of the same person.
                               */
                              `${leaveTypeName(p.leaveTypeCode)} — allotment ${LeaveBalanceService.totalEntitlement(s)}` +
                              `, used ${LeaveBalanceService.totalUsed(s)}` +
                              (LeaveBalanceService.historicalUsedOf(s) > 0
                                ? ` (${LeaveBalanceService.historicalUsedOf(s)} recorded by HR before go-live)`
                                : '') +
                              (s.pending ? `, pending ${s.pending}` : '') +
                              `, balance ${s.balance}` +
                              /*
                               * Expiry goes in the tooltip, not the cell. These cells are 82px
                               * wide (68px under 1180px) and already stack three lines, so a
                               * date cannot fit — but HR fielding "where did my Comp-Off go"
                               * needs the deadline reachable from the sheet they are reading,
                               * and the ⏳ below tells them it is there to hover for.
                               */
                              (expiryTooltip(s.expiringGrants) ? `\n${expiryTooltip(s.expiringGrants)}` : '')
                            }
                          >
                            {/* Entitlement, not bare `granted`: balanceLevel divides by what
                                it is given, and Comp-Off — earned, never granted — has a
                                `granted` of 0 and so always coloured as healthy. */}
                            <span className="lb-doj-bal" data-level={balanceLevel(s.balance, LeaveBalanceService.totalEntitlement(s))}>
                              {s.balance}
                            </span>
                            <span className="lb-doj-sub">
                              {LeaveBalanceService.totalEntitlement(s)} · {LeaveBalanceService.totalUsed(s)}
                              {/* The affordance for the expiry detail in this cell's tooltip.
                                  A glyph rather than the date itself: there is no room here for
                                  a date, and a tooltip nothing points at is a tooltip nobody
                                  finds. aria-label carries the same fact for screen readers,
                                  which never see a title on a <td>. */}
                              {expirySummary(s.expiringGrants) && (
                                <span className="lb-doj-expiry-flag" aria-label={expirySummary(s.expiringGrants)}> ⏳</span>
                              )}
                            </span>
                            {s.pending > 0 && <span className="lb-doj-pending">{s.pending} pending</span>}
                          </td>
                        );
                      })}
                      <td className="lb-doj-num lb-doj-total">
                        {/* A real 0 and "not calculated yet" must not look the same: snaps is
                            empty until the balances land, and reducing it gives 0. */}
                        {dojMissing
                          ? '—'
                          : snaps.length === 0 && loadingBalances
                            ? <span className="lb-doj-cell-loading">·</span>
                            : Math.round(totalBalance * 10) / 10}
                      </td>
                      <td className="lb-doj-actions">
                        <button
                          type="button"
                          className="lb-doj-act-btn"
                          onClick={() => { setAdjustEmployee(e); setAdjustTypeCode(''); }}
                          title={`Adjust a leave balance for ${e.fullName || e.name}`}
                        >
                          Adjust
                        </button>
                        {/* No View button. The employee's name opens the same profile drawer, so a
                            second control in the row did the same thing twice and cost the sheet a
                            column of width it needs for the leave types. */}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Kept after the table as well as the hint above it, so the state is visible whether
              the reader is at the top or has scrolled to the bottom of a long sheet. */}
          {loadingBalances && <p className="lb-ineligible-desc">Still calculating balances…</p>}

          {/* Month-wise breakdown when a single employee is selected */}
          {singleEmployee && (
            singleEmployee.dateOfJoining ? (
              <div style={{ marginTop: 20 }}>
                <h4>Month-wise Accrual — {singleEmployee.fullName || singleEmployee.name} (DOJ {singleEmployee.dateOfJoining})</h4>
                <div className="lb-table-scroll">
                  <table className="lb-history-table">
                    <thead>
                      <tr>
                        <th>Leave Type</th>
                        {MONTH_ABBR.map((m) => <th key={m}>{m}</th>)}
                        <th>Allotment</th>
                        <th>Used</th>
                        <th>Pending</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flatPolicies.map((p) => {
                        /*
                         * The same two-step the balance maths does: a policy allocation is
                         * read at that policy’s accrual frequency, a leave-type default is
                         * annual. Resolved here so this table and the Entitlement column
                         * beside it cannot answer the question differently.
                         */
                        const allocation = singleEmployeePolicy?.allocations.find(
                          (a) => a.leaveTypeCode.toUpperCase() === p.leaveTypeCode.toUpperCase()
                        );
                        const otherRate = allocation
                          ? LeavePolicyService.perMonthRate(
                              allocation.grantedPerYear,
                              singleEmployeePolicy?.accrualFrequency
                            )
                          : LeavePolicyService.perMonthRate(p.grantedPerYear, 'YEARLY');
                        const series = monthlyCumulativeNew(
                          singleEmployee.dateOfJoining as string,
                          year,
                          p.leaveTypeCode,
                          otherRate,
                          singleEmployeeRates
                        );
                        const s = (balancesByEmail[singleEmployee.email] || []).find((x) => x.leaveTypeCode.toUpperCase() === p.leaveTypeCode.toUpperCase());
                        return (
                          <tr key={p.leaveTypeCode}>
                            <td><strong>{leaveTypeName(p.leaveTypeCode)}</strong></td>
                            {series.map((v, i) => <td key={i}>{v}</td>)}
                            {/* Entitlement and the real used figure, so the three columns
                                add up: entitlement − used − pending = balance. `granted` and
                                `consumed` did not, once HR had recorded pre-go-live leave. */}
                            <td><strong>{s ? LeaveBalanceService.totalEntitlement(s) : 0}</strong></td>
                            <td title={s ? usedTooltip(s) : undefined}>{s ? LeaveBalanceService.totalUsed(s) : 0}</td>
                            <td>{s?.pending ?? 0}</td>
                            <td><strong>{s?.balance ?? 0}</strong></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="lb-ineligible-desc" style={{ marginTop: 16 }}>
                Date of Joining is not set for {singleEmployee.fullName || singleEmployee.name}. Set it in User Management to see the month-wise accrual.
              </p>
            )
          )}
        </>
      )}

      {/* Employee details — the existing drawer, reused rather than reimplemented, so this sheet
          and the Team Leave Matrix show one employee the same way. */}
      <EmployeeLeaveProfileDrawer
        isOpen={!!profileEmployee}
        onClose={() => setProfileEmployee(null)}
        employee={profileEmployee}
        leaves={leaves}
        year={year}
      />

      <LeaveBalanceAdjustModal
        isOpen={!!adjustEmployee}
        onClose={() => setAdjustEmployee(null)}
        employee={adjustEmployee}
        year={year}
        snapshots={adjustEmployee ? balancesByEmail[adjustEmployee.email] || [] : []}
        leaveTypes={adjustLeaveTypes}
        // resolveActorId, not `user?.email || ''`: a federated Azure account can have no email
        // attribute at all, and the bare fallback wrote an empty author — the trail then reads
        // "Not recorded", which is unrecoverable, because nothing was stored to attribute later.
        // The sub is a poor label but a real identifier, and the trail resolves it back to a
        // person at render time. Same expression HR Operations uses, so the two entry points
        // cannot record authorship differently.
        updatedBy={resolveActorId(user)}
        initialLeaveTypeCode={adjustTypeCode}
        onSaved={handleAdjustSaved}
      />
    </div>
  );
};

export default LeavePoliciesPage;
